"use client";

import React, { useState, useRef, useEffect, useId, useMemo } from "react";
import { StyledDiv, WidgetPopoverDiv } from "./widgetAiPrompt.styles";
import cn from "classnames";
import apps from "../../../apps.json";
import { handleMobileRedirect } from "../../helpers/mobile-redirect";

export const WidgetAiPrompt = () => {
  const isAIEditorCampaign =
    typeof window !== "undefined" &&
    window.location.search.includes("utm_campaign=AIEDITOR2025");
  const [widgetOptions] = useState(
    !isAIEditorCampaign
      ? widgets.sort(() => Math.random() - 0.5).slice(0, 3)
      : widgets.slice(-3) // Last 3 widgets
  );
  const [selectedOption, setSelectedOption] = useState<string>(
    widgetOptions[0].slug
  );
  const [input, setInput] = useState<string>("");
  const [showWidgetPopover, setShowWidgetPopover] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const selectButtonRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Simplified cycling - alternate between 0 and 1 directly
  const [isContentVariation, setIsContentVariation] = useState(true);

  // Add cycle counter to force unique keys for each animation cycle
  const [cycleCounter, setCycleCounter] = useState(0);

  // Get appropriate placeholders based on selected widget and variation type
  const placeholder = useMemo(() => {
    const placeholders =
      widgetPlaceholders[selectedOption as keyof typeof widgetPlaceholders] ||
      defaultPlaceholders.map((p) =>
        p.replace(
          "widget",
          apps.find((a) => a.slug === selectedOption)?.name || "widget"
        )
      );

    // Always use index 0 for content variation and index 1 for style variation
    return placeholders[isContentVariation ? 0 : 1];
  }, [selectedOption, isContentVariation]);

  // Force a reset of typing animation when changing variations or widgets
  const animationKey = useMemo(() => {
    // Include the cycle counter to ensure each animation gets a unique key
    return cycleCounter;
  }, [cycleCounter]);

  // Use typing effect hook with the current placeholder and animation key
  const { displayText, phase } = useTypingEffect(
    placeholder,
    40,
    20,
    300,
    3000,
    animationKey
  );

  // Switch to next variation when the current animation cycle completes
  useEffect(() => {
    // Only toggle when we've finished deleting (completed full cycle)
    if (phase === "complete") {
      setIsContentVariation((prev) => !prev);
      setCycleCounter((prev) => prev + 1);
    }
  }, [phase]);

  // Reset to content variation when changing widget
  useEffect(() => {
    setIsContentVariation(true);
    setCycleCounter((prev) => prev + 1);
  }, [selectedOption]);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  function handleSubmit(e: any) {
    const editorUrl = `https://www.commoninja.com/${selectedOption}/editor?prompt=${encodeURIComponent(
      input
    )}`;
    if (handleMobileRedirect(e, editorUrl)) {
      return;
    }
    window.location.href = editorUrl;
  }

  function handleWidgetSelect(widgetSlug: string) {
    setSelectedOption(widgetSlug);
    setShowWidgetPopover(false);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Only close if clicking outside both the button and the popover
      const target = event.target as Element;
      const clickedInsidePopover = target.closest(".widget-popover") !== null;
      const clickedOnButton =
        selectButtonRef.current?.contains(target) || false;

      if (!clickedInsidePopover && !clickedOnButton) {
        setShowWidgetPopover(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <StyledDiv className="prompt-container">
      <div className="prompt-input">
        <textarea
          ref={textareaRef}
          placeholder={displayText}
          maxLength={300}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
      </div>
      <div className="prompt-options">
        {widgetOptions.map((w) => (
          <button
            key={w.slug}
            className={`option-button ${
              selectedOption === w.slug ? "active" : ""
            }`}
            onClick={() => setSelectedOption(w.slug)}
          >
            {w.icon && <span className="icon">{w.icon}</span>} {w.label}
          </button>
        ))}
        <span className="or">OR</span>
        <div className="select-widget-container">
          <button
            ref={selectButtonRef}
            className={`option-button ${
              !widgetOptions.map((w) => w.slug).includes(selectedOption)
                ? "active"
                : ""
            }`}
            onClick={() => setShowWidgetPopover(!showWidgetPopover)}
          >
            <span className="icon">
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.9619 1.39477C6.9619 1.02566 6.81525 0.671617 6.5542 0.41038C6.29291 0.149374 5.93881 0.00274658 5.56962 0.00274658H1.39277C1.02358 0.00274658 0.66948 0.149374 0.408195 0.41038C0.147142 0.671617 0.000488281 1.02566 0.000488281 1.39477V5.57086C0.000488281 5.93998 0.147142 6.29402 0.408195 6.55525C0.66948 6.81626 1.02358 6.96289 1.39277 6.96289H5.56962C5.93881 6.96289 6.29291 6.81626 6.5542 6.55525C6.81525 6.29402 6.9619 5.93998 6.9619 5.57086V1.39477Z"
                  fill="#D77142"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.9619 9.43188C6.9619 9.06276 6.81525 8.70873 6.5542 8.44749C6.29291 8.18648 5.93881 8.03986 5.56962 8.03986H1.39277C1.02358 8.03986 0.66948 8.18648 0.408195 8.44749C0.147142 8.70873 0.000488281 9.06276 0.000488281 9.43188V13.608C0.000488281 13.9771 0.147142 14.3311 0.408195 14.5924C0.66948 14.8534 1.02358 15 1.39277 15H5.56962C5.93881 15 6.29291 14.8534 6.5542 14.5924C6.81525 14.3311 6.9619 13.9771 6.9619 13.608V9.43188Z"
                  fill="#D77142"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M15.0005 9.43188C15.0005 9.06276 14.8538 8.70873 14.5928 8.44749C14.3315 8.18648 13.9774 8.03986 13.6082 8.03986H9.43135C9.06216 8.03986 8.70805 8.18648 8.44677 8.44749C8.18572 8.70873 8.03906 9.06276 8.03906 9.43188V13.608C8.03906 13.9771 8.18572 14.3311 8.44677 14.5924C8.70805 14.8534 9.06216 15 9.43135 15H13.6082C13.9774 15 14.3315 14.8534 14.5928 14.5924C14.8538 14.3311 15.0005 13.9771 15.0005 13.608V9.43188Z"
                  fill="#D77142"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.4943 0.404058C11.955 -0.134686 11.0808 -0.134686 10.5415 0.404058C9.93846 1.0074 9.0436 1.90164 8.44061 2.50497C7.90131 3.04418 7.90131 3.91826 8.44061 4.45701C9.0436 5.06034 9.93846 5.95458 10.5415 6.55792C11.0808 7.09712 11.955 7.09712 12.4943 6.55792C13.0973 5.95458 13.9922 5.06034 14.5951 4.45701C15.1344 3.91826 15.1344 3.04418 14.5951 2.50497C13.9922 1.90164 13.0973 1.0074 12.4943 0.404058Z"
                  fill="#D77142"
                />
              </svg>
            </span>{" "}
            {!widgetOptions.map((w) => w.slug).includes(selectedOption)
              ? apps.find((a) => a.slug === selectedOption)?.name
              : "Select Widget"}
          </button>
          <WidgetPopover
            show={showWidgetPopover}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSelect={handleWidgetSelect}
          />
        </div>
        <button
          onClick={handleSubmit}
          className="submit-button"
          disabled={input.length < 5}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.428 6.08465L1.11548 0.0785018C1.00958 0.0268482 0.893308 0 0.775485 0C0.3472 0 0 0.3472 0 0.775485L0 0.797866C0 0.901924 0.0127591 1.00559 0.0379973 1.10655L1.21907 5.83081C1.25133 5.95986 1.36043 6.05519 1.49262 6.06989L6.68379 6.64669C6.86382 6.66667 7 6.81883 7 7C7 7.18117 6.86382 7.33333 6.68379 7.35331L1.49262 7.93011C1.36043 7.94481 1.25133 8.04014 1.21907 8.16919L0.0379973 12.8934C0.0127591 12.9944 0 13.0981 0 13.2021L0 13.2245C0 13.6528 0.3472 14 0.775485 14C0.893308 14 1.00958 13.9731 1.11548 13.9215L13.428 7.91535C13.778 7.74467 14 7.38939 14 7C14 6.61061 13.778 6.25533 13.428 6.08465Z"
              fill="white"
            />
          </svg>
        </button>
      </div>
    </StyledDiv>
  );
};

interface IWidgetPopoverProps {
  show: boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  onSelect: (widgetSlug: string) => void;
}

const WidgetPopover = ({
  show,
  searchTerm,
  setSearchTerm,
  onSelect,
}: IWidgetPopoverProps) => {
  const wantedApps = apps.filter((w) => w.slug !== "brackets");
  const filteredWidgets = searchTerm
    ? wantedApps.filter((w) =>
        w.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : wantedApps;

  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  // Prevent clicks within the popover from closing it
  function handlePopoverClick(e: React.MouseEvent) {
    e.stopPropagation();
  }

  return (
    <WidgetPopoverDiv
      ref={popoverRef}
      className={cn({ show }, "widget-popover")}
      id={popoverId}
      onClick={handlePopoverClick}
    >
      <div className="widget-search">
        <div className="search-icon">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
              stroke="#8D8D8D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 21L16.65 16.65"
              stroke="#8D8D8D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Widget name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="widget-list">
        {filteredWidgets.length === 0 && (
          <p style={{ margin: "10px 0", fontSize: 14 }}>No widgets found</p>
        )}
        {filteredWidgets.map((widget) => (
          <div
            key={widget.slug}
            className={`widget-item`}
            onClick={() => onSelect(widget.slug)}
          >
            <span className="widget-name">{widget.name}</span>
          </div>
        ))}
      </div>
    </WidgetPopoverDiv>
  );
};

// Custom hook for typing animation with delete effect
const useTypingEffect = (
  text: string,
  typingSpeed = 50,
  deletingSpeed = 30,
  initialDelay = 500,
  pauseDelay = 2000,
  key: number | string = 0
) => {
  const [displayText, setDisplayText] = useState("");
  const [phase, setPhase] = useState<
    "typing" | "pausing" | "deleting" | "complete"
  >("typing");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset when text or key changes
  useEffect(() => {
    setDisplayText("");
    setCurrentIndex(0);
    setPhase("typing");
  }, [text, key]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (phase === "typing") {
      if (currentIndex === 0) {
        // Initial delay before starting to type
        timeout = setTimeout(() => {
          setCurrentIndex(1);
          setDisplayText(text.substring(0, 1));
        }, initialDelay);
      } else if (currentIndex < text.length) {
        // Continue typing each character
        timeout = setTimeout(() => {
          setDisplayText(text.substring(0, currentIndex + 1));
          setCurrentIndex((prev) => prev + 1);
        }, typingSpeed);
      } else {
        // When done typing, pause before deleting
        timeout = setTimeout(() => {
          setPhase("deleting");
        }, pauseDelay);
      }
    } else if (phase === "deleting") {
      if (currentIndex > 0) {
        // Delete characters one by one
        timeout = setTimeout(() => {
          setDisplayText(text.substring(0, currentIndex - 1));
          setCurrentIndex((prev) => prev - 1);
        }, deletingSpeed);
      } else {
        // When done deleting, mark as complete
        setPhase("complete");
      }
    }

    return () => clearTimeout(timeout);
  }, [
    currentIndex,
    initialDelay,
    phase,
    text,
    typingSpeed,
    deletingSpeed,
    pauseDelay,
  ]);

  return {
    displayText,
    isTyping: phase === "typing" || phase === "deleting",
    phase,
  };
};

const widgets = [
  {
    label: "Quiz",
    slug: "quiz",
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.1632 -0.000144958C11.4316 -0.000144958 12.4639 0.997234 12.464 2.22251C12.464 2.3809 12.4457 2.54231 12.4083 2.70298C13.6618 3.08472 14.5274 4.21761 14.5274 5.49693C14.5274 5.97474 14.4048 6.44547 14.1749 6.86509C14.6843 7.35278 15.0001 8.02869 15.0001 8.77427C15 9.99114 14.1589 11.021 13.0089 11.3514C13.0491 11.5032 13.0704 11.6597 13.0704 11.8182C13.0703 12.8501 12.2013 13.6892 11.1329 13.6893C11.0118 13.6893 10.8888 13.6772 10.7667 13.6542C10.7211 14.3887 10.0883 14.9715 9.31651 14.9715C8.57502 14.9715 7.96244 14.4819 7.8751 13.745L7.86436 13.5936V11.2469C7.86434 11.203 7.85658 11.1584 7.85655 11.1131C7.85655 11.1007 7.86427 11.0892 7.86436 11.078V8.88657C7.48331 8.91103 7.10756 8.9903 6.74424 9.10825C6.57929 9.16195 6.40246 9.19042 6.21885 9.19028C5.26952 9.19036 4.5073 8.41597 4.57335 7.48521C4.50714 6.55433 5.26943 5.77934 6.21885 5.77915C6.40248 5.77928 6.57914 5.80858 6.74424 5.86216C7.10749 5.98009 7.48339 6.05741 7.86436 6.08189V0.877785C7.86436 0.393885 8.27099 -3.12036e-05 8.77159 -0.000144958H10.1632Z"
          fill="#E174D6"
        />
        <path
          d="M6.22852 0.0255432C6.72915 0.025654 7.13645 0.418956 7.13672 0.902496V5.30777C7.03509 5.27963 6.93339 5.25152 6.83301 5.2189C6.602 5.14378 6.35424 5.10269 6.09668 5.10269C4.76645 5.10277 3.69887 6.1889 3.79199 7.49332C3.69889 8.79766 4.76655 9.88376 6.09668 9.88394C6.35382 9.88394 6.60165 9.84283 6.83301 9.76773C6.93343 9.73513 7.03504 9.70603 7.13672 9.67789V13.6095C7.13672 14.3773 6.48025 15.0001 5.68359 15.0002C4.91175 15.0002 4.27886 14.4156 4.2334 13.6808C4.11118 13.7037 3.98854 13.716 3.86719 13.716C2.79897 13.7158 1.9297 12.8761 1.92969 11.8439C1.92969 11.6855 1.95169 11.5291 1.99219 11.3771C0.841968 11.0469 0.000142147 10.0169 0 8.79996C0 8.05426 0.316055 7.37823 0.825195 6.89078C0.595588 6.47097 0.472696 6.00048 0.472656 5.52261C0.472656 4.24315 1.33899 3.1104 2.59277 2.72867C2.55551 2.56798 2.53613 2.40672 2.53613 2.2482C2.53615 1.02287 3.56873 0.0255432 4.83691 0.0255432H6.22852Z"
          fill="#E174D6"
        />
      </svg>
    ),
  },
  {
    label: "Form",
    slug: "form",
    icon: (
      <svg
        width="13"
        height="15"
        viewBox="0 0 13 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.9744 3.51428H10.6665L7.55469 0.257141V3.07496C7.55469 3.31735 7.74283 3.51428 7.9744 3.51428Z"
          fill="#10855C"
        />
        <path
          d="M12.9062 9.0391L12.2664 8.3695C12.1417 8.23881 11.9392 8.23871 11.8143 8.3693C11.8143 8.3694 11.8142 8.3694 11.8142 8.3695L7.97555 12.3874C7.91561 12.4502 7.88184 12.5353 7.88184 12.6241V13.2938C7.88184 13.4787 8.02504 13.6286 8.20174 13.6286H8.84154C8.92637 13.6286 9.00768 13.5933 9.06762 13.5306L12.9062 9.51261C13.0311 9.38192 13.0312 9.17009 12.9064 9.0393C12.9063 9.0393 12.9062 9.0392 12.9062 9.0391Z"
          fill="#10855C"
        />
        <path
          d="M6.67676 3.07617C6.67676 3.80301 7.23853 4.3943 7.92871 4.39453H10.8506V8.95312L7.77246 12.1758C7.65929 12.2946 7.59476 12.4556 7.59473 12.624V13.2939C7.59478 13.6445 7.86725 13.9287 8.20215 13.9287H8.8418C9.00245 13.9286 9.15687 13.8621 9.27051 13.7432L10.8506 12.0879V13.6816C10.8506 14.4085 10.2889 14.9999 9.59863 15H1.25195C0.561597 15 0 14.4086 0 13.6816V1.31836C1.85489e-08 0.591391 0.561597 6.44505e-06 1.25195 0H6.67676V3.07617ZM10.8506 11.6631L9.06738 13.5303C9.00751 13.5929 8.92653 13.6288 8.8418 13.6289H8.20215C8.02549 13.6289 7.88189 13.4787 7.88184 13.2939V12.624C7.88187 12.5353 7.91575 12.4505 7.97559 12.3877L10.8506 9.37793V11.6631ZM2.08691 9.56152C1.85649 9.56152 1.66902 9.75835 1.66895 10.001C1.66895 10.2437 1.85644 10.4404 2.08691 10.4404C2.31725 10.4403 2.50391 10.2436 2.50391 10.001C2.50383 9.75845 2.3172 9.5617 2.08691 9.56152ZM3.75586 9.56152C3.52525 9.56154 3.33894 9.75815 3.33887 10.001C3.33887 10.2439 3.52521 10.4404 3.75586 10.4404H7.92871C8.15938 10.4404 8.34668 10.2439 8.34668 10.001C8.34661 9.75814 8.15933 9.56152 7.92871 9.56152H3.75586ZM2.08691 7.80371C1.85649 7.80371 1.66902 8.00053 1.66895 8.24316C1.66895 8.48586 1.85644 8.68262 2.08691 8.68262C2.31725 8.68244 2.50391 8.48575 2.50391 8.24316C2.50383 8.00064 2.3172 7.80389 2.08691 7.80371ZM3.75586 7.80371C3.52525 7.80373 3.33894 8.00034 3.33887 8.24316C3.33887 8.48605 3.52521 8.6826 3.75586 8.68262H7.92871C8.15938 8.68262 8.34668 8.48606 8.34668 8.24316C8.34661 8.00033 8.15933 7.80371 7.92871 7.80371H3.75586ZM2.08691 6.0459C1.85649 6.0459 1.66902 6.24272 1.66895 6.48535C1.66895 6.72805 1.85644 6.9248 2.08691 6.9248C2.31725 6.92463 2.50391 6.72794 2.50391 6.48535C2.50383 6.24283 2.3172 6.04607 2.08691 6.0459ZM3.75586 6.0459C3.52525 6.04592 3.33894 6.24253 3.33887 6.48535C3.33887 6.72824 3.52521 6.92479 3.75586 6.9248H7.92871C8.15938 6.9248 8.34668 6.72825 8.34668 6.48535C8.34661 6.24252 8.15933 6.0459 7.92871 6.0459H3.75586Z"
          fill="#10855C"
        />
      </svg>
    ),
  },
  {
    label: "Booking",
    slug: "booking",
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.3086 6.73828C11.6322 6.73828 11.8945 6.47595 11.8945 6.15234C11.8945 5.82874 11.6322 5.56641 11.3086 5.56641C10.985 5.56641 10.7227 5.82874 10.7227 6.15234C10.7227 6.47595 10.985 6.73828 11.3086 6.73828Z"
          fill="#A5E838"
        />
        <path
          d="M12.6562 1.17188H11.8945V0.585938C11.8945 0.262324 11.6322 0 11.3086 0C10.985 0 10.7227 0.262324 10.7227 0.585938V1.17188H8.05664V0.585938C8.05664 0.262324 7.79432 0 7.4707 0C7.14709 0 6.88477 0.262324 6.88477 0.585938V1.17188H4.24805V0.585938C4.24805 0.262324 3.98572 0 3.66211 0C3.3385 0 3.07617 0.262324 3.07617 0.585938V1.17188H2.34375C1.05141 1.17188 0 2.22328 0 3.51562V12.6562C0 13.9486 1.05141 15 2.34375 15H6.82617C7.14979 15 7.41211 14.7377 7.41211 14.4141C7.41211 14.0904 7.14979 13.8281 6.82617 13.8281H2.34375C1.69758 13.8281 1.17188 13.3024 1.17188 12.6562V3.51562C1.17188 2.86945 1.69758 2.34375 2.34375 2.34375H3.07617V2.92969C3.07617 3.2533 3.3385 3.51562 3.66211 3.51562C3.98572 3.51562 4.24805 3.2533 4.24805 2.92969V2.34375H6.88477V2.92969C6.88477 3.2533 7.14709 3.51562 7.4707 3.51562C7.79432 3.51562 8.05664 3.2533 8.05664 2.92969V2.34375H10.7227V2.92969C10.7227 3.2533 10.985 3.51562 11.3086 3.51562C11.6322 3.51562 11.8945 3.2533 11.8945 2.92969V2.34375H12.6562C13.3024 2.34375 13.8281 2.86945 13.8281 3.51562V6.85547C13.8281 7.17908 14.0904 7.44141 14.4141 7.44141C14.7377 7.44141 15 7.17908 15 6.85547V3.51562C15 2.22328 13.9486 1.17188 12.6562 1.17188Z"
          fill="#A5E838"
        />
        <path
          d="M8.75977 6.73828C9.08337 6.73828 9.3457 6.47595 9.3457 6.15234C9.3457 5.82874 9.08337 5.56641 8.75977 5.56641C8.43616 5.56641 8.17383 5.82874 8.17383 6.15234C8.17383 6.47595 8.43616 6.73828 8.75977 6.73828Z"
          fill="#A5E838"
        />
        <path
          d="M6.21094 9.28711C6.53454 9.28711 6.79688 9.02478 6.79688 8.70117C6.79688 8.37757 6.53454 8.11523 6.21094 8.11523C5.88733 8.11523 5.625 8.37757 5.625 8.70117C5.625 9.02478 5.88733 9.28711 6.21094 9.28711Z"
          fill="#A5E838"
        />
        <path
          d="M3.66211 6.73828C3.98571 6.73828 4.24805 6.47595 4.24805 6.15234C4.24805 5.82874 3.98571 5.56641 3.66211 5.56641C3.33851 5.56641 3.07617 5.82874 3.07617 6.15234C3.07617 6.47595 3.33851 6.73828 3.66211 6.73828Z"
          fill="#A5E838"
        />
        <path
          d="M3.66211 9.28711C3.98571 9.28711 4.24805 9.02478 4.24805 8.70117C4.24805 8.37757 3.98571 8.11523 3.66211 8.11523C3.33851 8.11523 3.07617 8.37757 3.07617 8.70117C3.07617 9.02478 3.33851 9.28711 3.66211 9.28711Z"
          fill="#A5E838"
        />
        <path
          d="M3.66211 11.8359C3.98571 11.8359 4.24805 11.5736 4.24805 11.25C4.24805 10.9264 3.98571 10.6641 3.66211 10.6641C3.33851 10.6641 3.07617 10.9264 3.07617 11.25C3.07617 11.5736 3.33851 11.8359 3.66211 11.8359Z"
          fill="#A5E838"
        />
        <path
          d="M6.21094 11.8359C6.53454 11.8359 6.79688 11.5736 6.79688 11.25C6.79688 10.9264 6.53454 10.6641 6.21094 10.6641C5.88733 10.6641 5.625 10.9264 5.625 11.25C5.625 11.5736 5.88733 11.8359 6.21094 11.8359Z"
          fill="#A5E838"
        />
        <path
          d="M6.21094 6.73828C6.53454 6.73828 6.79688 6.47595 6.79688 6.15234C6.79688 5.82874 6.53454 5.56641 6.21094 5.56641C5.88733 5.56641 5.625 5.82874 5.625 6.15234C5.625 6.47595 5.88733 6.73828 6.21094 6.73828Z"
          fill="#A5E838"
        />
        <path
          d="M11.0445 12.8031C11.0112 12.8378 10.9657 12.8571 10.9184 12.8571C10.8712 12.8571 10.8257 12.8378 10.7923 12.8031L9.78541 11.7607C9.68091 11.6525 9.68091 11.4771 9.78541 11.3692L9.91149 11.2387C10.016 11.1305 10.1853 11.1305 10.2898 11.2387L10.9184 11.8894L12.6171 10.1311C12.7217 10.023 12.8911 10.023 12.9954 10.1311L13.1215 10.2617C13.226 10.3698 13.226 10.5452 13.1215 10.6532L11.0445 12.8031Z"
          fill="#A5E838"
        />
        <path
          d="M11.4521 7.90714C13.4068 7.90714 14.9971 9.49738 14.9971 11.4521C14.9971 13.4068 13.4068 14.997 11.4521 14.997C9.49746 14.997 7.90723 13.4068 7.90723 11.4521C7.90723 9.49738 9.49746 7.90714 11.4521 7.90714ZM11.4434 8.80753C9.98769 8.80753 8.80762 9.98761 8.80762 11.4433C8.80784 12.8987 9.98783 14.079 11.4434 14.079C12.8987 14.0788 14.0789 12.8986 14.0791 11.4433C14.0791 9.98774 12.8988 8.80775 11.4434 8.80753Z"
          fill="#A5E838"
        />
      </svg>
    ),
  },
  {
    label: "Timeline",
    slug: "timeline",
    icon: (
      <svg
        width="16"
        height="15"
        viewBox="0 0 16 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.75 3.125C15.4404 3.125 16 3.68466 16 4.375V10.625C16 11.3154 15.4404 11.875 14.75 11.875H4.94238C4.25218 11.8748 3.69238 11.3152 3.69238 10.625V4.375C3.69241 3.68478 4.2522 3.1252 4.94238 3.125H14.75ZM6.09863 9C5.85725 9.00026 5.66116 9.19606 5.66113 9.4375C5.66115 9.67895 5.85724 9.87474 6.09863 9.875H11.501C11.7424 9.87478 11.9385 9.67898 11.9385 9.4375C11.9384 9.19603 11.7424 9.00022 11.501 9H6.09863ZM6.09863 7.0498C5.8573 7.05007 5.66125 7.24594 5.66113 7.4873C5.66115 7.72875 5.85724 7.92454 6.09863 7.9248H9.67871C9.92033 7.9248 10.1162 7.72892 10.1162 7.4873C10.1161 7.24578 9.92027 7.0498 9.67871 7.0498H6.09863ZM6.09863 5.125C5.85723 5.12526 5.66113 5.32103 5.66113 5.5625C5.66115 5.80395 5.85724 5.99974 6.09863 6H12.8545C13.0961 5.99996 13.292 5.80409 13.292 5.5625C13.292 5.3209 13.0961 5.12504 12.8545 5.125H6.09863Z"
          fill="#3856A4"
        />
        <path
          d="M2.65711 7.31733L3.69238 6.35001L3.69238 8.65001L2.65711 7.68268C2.55135 7.58386 2.55135 7.41615 2.65711 7.31733Z"
          fill="#3856A4"
        />
        <ellipse
          cx="0.86154"
          cy="7.49997"
          rx="0.86154"
          ry="0.875"
          fill="#3856A4"
        />
        <ellipse
          cx="0.86154"
          cy="11.75"
          rx="0.86154"
          ry="0.875"
          fill="#3856A4"
        />
        <rect
          x="0.59082"
          y="2.5"
          width="0.541539"
          height="12.5"
          rx="0.27077"
          fill="#3856A4"
        />
        <rect
          x="0.59082"
          width="0.541539"
          height="1.75"
          rx="0.27077"
          fill="#3856A4"
        />
      </svg>
    ),
  },
  {
    label: "FAQ",
    slug: "faq",
    icon: (
      <svg
        width="17"
        height="15"
        viewBox="0 0 17 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.7857 5.5C16.4563 5.5 17 6.05964 17 6.75V11.75C17 12.4404 16.4563 13 15.7857 13H14.5714V15L12.6286 13H8.5C7.82937 13 7.28571 12.4404 7.28571 11.75V8.05H9.08286C9.75349 8.05 10.2971 7.49036 10.2971 6.8V5.5H15.7857ZM9.71429 10.625C9.5131 10.625 9.35 10.7929 9.35 11C9.35 11.2071 9.5131 11.375 9.71429 11.375H14.5714L14.5808 11.3749C14.7777 11.3698 14.9357 11.2039 14.9357 11C14.9357 10.7961 14.7777 10.6302 14.5808 10.6251L14.5714 10.625H9.71429ZM9.71429 8.875C9.5131 8.875 9.35 9.04289 9.35 9.25C9.35 9.45711 9.5131 9.625 9.71429 9.625H14.5714L14.5808 9.62488C14.7777 9.61975 14.9357 9.45387 14.9357 9.25C14.9357 9.04613 14.7777 8.88025 14.5808 8.87512L14.5714 8.875H9.71429ZM11.3657 7.125C11.1645 7.125 11.0014 7.29289 11.0014 7.5C11.0014 7.70711 11.1645 7.875 11.3657 7.875H14.5714L14.5808 7.87488C14.7777 7.86975 14.9357 7.70387 14.9357 7.5C14.9357 7.29613 14.7777 7.13025 14.5808 7.12512L14.5714 7.125H11.3657Z"
          fill="#9385EC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8.5 0C9.17063 0 9.71429 0.559644 9.71429 1.25V6.25C9.71429 6.94036 9.17063 7.5 8.5 7.5H4.37143L2.42857 9.5V7.5H1.21429C0.543654 7.5 0 6.94036 0 6.25V1.25C8.47619e-08 0.559644 0.543654 9.66389e-10 1.21429 0H8.5ZM4.78834 5.02617C4.71816 5.02617 4.65342 5.04383 4.59415 5.07913C4.53644 5.11443 4.49043 5.16256 4.45612 5.22354C4.42181 5.2829 4.40466 5.34949 4.40466 5.42329C4.40466 5.49549 4.42181 5.5621 4.45612 5.62307C4.49043 5.68244 4.53645 5.72976 4.59415 5.76506C4.65342 5.80197 4.71816 5.82043 4.78834 5.82043C4.86008 5.82043 4.9248 5.80197 4.98251 5.76506C5.04178 5.72976 5.08857 5.68244 5.12289 5.62307C5.1572 5.5621 5.17435 5.49549 5.17435 5.42329C5.17435 5.34949 5.1572 5.2829 5.12289 5.22354C5.08857 5.16256 5.04178 5.11443 4.98251 5.07913C4.9248 5.04383 4.86008 5.02617 4.78834 5.02617ZM4.83044 1.7C4.623 1.7 4.43194 1.74252 4.25726 1.82756C4.08414 1.911 3.94063 2.03293 3.82678 2.19338C3.71448 2.35223 3.64509 2.54397 3.61857 2.7686L4.10519 2.86729C4.12546 2.66833 4.19957 2.51349 4.32746 2.40278C4.45535 2.29047 4.62067 2.2343 4.82342 2.2343C4.96691 2.2343 5.0917 2.26077 5.19776 2.31372C5.30381 2.36667 5.38568 2.44209 5.44339 2.53997C5.5011 2.63784 5.52996 2.75497 5.52996 2.89136C5.52996 3.03416 5.49799 3.15612 5.43405 3.2572C5.37166 3.35828 5.28353 3.43528 5.16968 3.48823C5.05582 3.53958 4.92169 3.56526 4.76728 3.56526C4.72049 3.56526 4.67603 3.56285 4.63392 3.55803C4.59181 3.55161 4.55438 3.54521 4.52163 3.53879V4.59055H5.06205V4.06587C5.25545 4.0418 5.42858 3.97522 5.58142 3.86611C5.73583 3.75701 5.85749 3.61821 5.9464 3.44973C6.0353 3.27965 6.07975 3.09434 6.07975 2.89377C6.07975 2.67395 6.02593 2.47419 5.91831 2.29448C5.81226 2.11317 5.66487 1.96876 5.47614 1.86125C5.28898 1.75375 5.07375 1.7 4.83044 1.7Z"
          fill="#9385EC"
        />
      </svg>
    ),
  },
  {
    label: "Poll",
    slug: "poll",
    icon: (
      <svg
        width="17"
        height="15"
        viewBox="0 0 17 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.39357 2.21457H1.24929C0.918059 2.2149 0.600496 2.34246 0.366283 2.56926C0.13207 2.79606 0.00034036 3.10358 0 3.42432V3.79389C0 3.8214 0.0112883 3.84779 0.0313814 3.86725C0.0514746 3.88671 0.0787268 3.89764 0.107143 3.89764H3.53571C3.56413 3.89764 3.59138 3.88671 3.61148 3.86725C3.63157 3.84779 3.64286 3.8214 3.64286 3.79389V3.42432C3.64252 3.10358 3.51079 2.79606 3.27657 2.56926C3.04236 2.34246 2.7248 2.2149 2.39357 2.21457Z"
          fill="#38C747"
        />
        <path
          d="M1.82128 1.83071C2.34983 1.83071 2.7783 1.42089 2.7783 0.915354C2.7783 0.409818 2.34983 0 1.82128 0C1.29273 0 0.864258 0.409818 0.864258 0.915354C0.864258 1.42089 1.29273 1.83071 1.82128 1.83071Z"
          fill="#38C747"
        />
        <path
          d="M2.39357 7.76575H1.24929C0.918059 7.76608 0.600496 7.89364 0.366283 8.12044C0.13207 8.34724 0.00034036 8.65475 0 8.9755V9.34507C0 9.37258 0.0112883 9.39897 0.0313814 9.41843C0.0514746 9.43789 0.0787268 9.44882 0.107143 9.44882H3.53571C3.56413 9.44882 3.59138 9.43789 3.61148 9.41843C3.63157 9.39897 3.64286 9.37258 3.64286 9.34507V8.9755C3.64252 8.65475 3.51079 8.34724 3.27657 8.12044C3.04236 7.89364 2.7248 7.76608 2.39357 7.76575Z"
          fill="#38C747"
        />
        <path
          d="M1.82128 7.38189C2.34983 7.38189 2.7783 6.97207 2.7783 6.46653C2.7783 5.961 2.34983 5.55118 1.82128 5.55118C1.29273 5.55118 0.864258 5.961 0.864258 6.46653C0.864258 6.97207 1.29273 7.38189 1.82128 7.38189Z"
          fill="#38C747"
        />
        <path
          d="M2.39357 13.3169H1.24929C0.918059 13.3173 0.600496 13.4448 0.366283 13.6716C0.13207 13.8984 0.00034036 14.2059 0 14.5267V14.8962C0 14.9238 0.0112883 14.9502 0.0313814 14.9696C0.0514746 14.9891 0.0787268 15 0.107143 15H3.53571C3.56413 15 3.59138 14.9891 3.61148 14.9696C3.63157 14.9502 3.64286 14.9238 3.64286 14.8962V14.5267C3.64252 14.2059 3.51079 13.8984 3.27657 13.6716C3.04236 13.4448 2.7248 13.3173 2.39357 13.3169Z"
          fill="#38C747"
        />
        <path
          d="M1.82128 12.9331C2.34983 12.9331 2.7783 12.5232 2.7783 12.0177C2.7783 11.5122 2.34983 11.1024 1.82128 11.1024C1.29273 11.1024 0.864258 11.5122 0.864258 12.0177C0.864258 12.5232 1.29273 12.9331 1.82128 12.9331Z"
          fill="#38C747"
        />
        <rect
          x="13.8428"
          y="0.188965"
          width="3.54331"
          height="8.5"
          rx="0.944882"
          transform="rotate(90 13.8428 0.188965)"
          fill="#38C747"
        />
        <rect
          x="17"
          y="5.74017"
          width="3.54331"
          height="11.6571"
          rx="0.944882"
          transform="rotate(90 17 5.74017)"
          fill="#38C747"
        />
        <rect
          x="11.4141"
          y="11.2677"
          width="3.54331"
          height="6.07143"
          rx="0.944882"
          transform="rotate(90 11.4141 11.2677)"
          fill="#38C747"
        />
      </svg>
    ),
  },
  {
    label: "Countdown Bar",
    slug: "countdown-bar",
    icon: (
      <svg
        width="13"
        height="15"
        viewBox="0 0 13 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.03906 2.16892V1.07107H7.58035C7.72391 1.07107 7.86159 1.01465 7.96311 0.914218C8.06462 0.813786 8.12165 0.67757 8.12165 0.535537C8.12165 0.393503 8.06462 0.257288 7.96311 0.156855C7.86159 0.0564224 7.72391 0 7.58035 0H5.41517C5.27161 0 5.13393 0.0564224 5.03242 0.156855C4.9309 0.257288 4.87387 0.393503 4.87387 0.535537C4.87387 0.67757 4.9309 0.813786 5.03242 0.914218C5.13393 1.01465 5.27161 1.07107 5.41517 1.07107H5.95647V2.16892C4.61162 2.27835 3.33547 2.80286 2.30813 3.66843L1.84803 3.21322L2.01042 3.05791C2.11235 2.95707 2.16961 2.8203 2.16961 2.67768C2.16961 2.53507 2.11235 2.3983 2.01042 2.29745C1.90849 2.19661 1.77025 2.13996 1.6261 2.13996C1.48195 2.13996 1.34371 2.19661 1.24178 2.29745L0.159191 3.36853C0.0572626 3.46937 0 3.60614 0 3.74876C0 3.89137 0.0572626 4.02814 0.159191 4.12899C0.261118 4.22983 0.399362 4.28648 0.54351 4.28648C0.687658 4.28648 0.825902 4.22983 0.92783 4.12899L1.08481 3.96833L1.54491 4.42353C0.604268 5.52386 0.0647438 6.90506 0.0129971 8.34526C-0.0387496 9.78546 0.400285 11.2011 1.25958 12.3647C2.11887 13.5283 3.34855 14.3724 4.75109 14.7614C6.15362 15.1504 7.64761 15.0618 8.99307 14.5096C10.3385 13.9575 11.4574 12.974 12.1699 11.717C12.8824 10.4601 13.1472 9.0027 12.9217 7.57896C12.6963 6.15521 11.9937 4.84775 10.9268 3.86659C9.85996 2.88544 8.49072 2.28753 7.03906 2.16892ZM6.49776 13.924C5.42718 13.924 4.38064 13.6099 3.49048 13.0214C2.60033 12.433 1.90654 11.5966 1.49684 10.618C1.08715 9.63943 0.979954 8.56264 1.18881 7.52381C1.39767 6.48497 1.91321 5.53073 2.67022 4.78177C3.42724 4.03281 4.39174 3.52276 5.44175 3.31612C6.49176 3.10948 7.58012 3.21554 8.56921 3.62087C9.5583 4.02621 10.4037 4.71262 10.9985 5.5933C11.5933 6.47399 11.9107 7.50939 11.9107 8.56859C11.9107 9.27186 11.7707 9.96825 11.4987 10.618C11.2267 11.2677 10.8279 11.8581 10.3253 12.3554C9.82266 12.8527 9.22594 13.2472 8.56921 13.5163C7.91248 13.7854 7.2086 13.924 6.49776 13.924Z"
          fill="#6DAEFA"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M3.9605 4.81202C4.7123 4.3152 5.59619 4.05002 6.50038 4.05002C7.71286 4.05002 8.87568 4.52638 9.73304 5.37431C10.5904 6.22225 11.072 7.37229 11.072 8.57145C11.072 9.4657 10.8039 10.3399 10.3016 11.0834C9.79924 11.827 9.08524 12.4065 8.24988 12.7487C7.41452 13.0909 6.49531 13.1805 5.60849 13.006C4.72167 12.8315 3.90708 12.4009 3.26772 11.7686C2.62836 11.1362 2.19295 10.3306 2.01656 9.45353C1.84016 8.57646 1.93069 7.66735 2.27671 6.84117C2.62273 6.01499 3.20869 5.30884 3.9605 4.81202ZM8.4211 10.442C8.5462 10.4341 8.66302 10.3775 8.74592 10.2845C8.78761 10.2382 8.8196 10.1843 8.84004 10.1257C8.86049 10.0672 8.86897 10.0052 8.86501 9.94336C8.86105 9.88154 8.84472 9.82111 8.81697 9.76557C8.78921 9.71004 8.75059 9.66051 8.70333 9.61985L6.97128 8.10341V6.23125C6.97128 6.10712 6.92142 5.98807 6.83267 5.9003C6.74392 5.81253 6.62355 5.76322 6.49804 5.76322C6.37253 5.76322 6.25216 5.81253 6.16341 5.9003C6.07466 5.98807 6.0248 6.10712 6.0248 6.23125V8.3187C6.02489 8.38516 6.03928 8.45083 6.06702 8.51136C6.09477 8.57188 6.13522 8.62587 6.1857 8.66973L8.07866 10.3266C8.17284 10.4084 8.296 10.4499 8.4211 10.442Z"
          fill="#6DAEFA"
        />
      </svg>
    ),
  },
  {
    label: "Image Slider",
    slug: "image-slider",
    icon: (
      <svg
        width="26"
        height="15"
        viewBox="0 0 26 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.1719 1.875C21.1719 0.839466 22.0113 0 23.0469 0H26.0004V15H23.0469C22.0113 15 21.1719 14.1605 21.1719 13.125V1.875Z"
          fill="#EB7B58"
        />
        <path
          d="M0 0H2.95357C3.9891 0 4.82857 0.839466 4.82857 1.875V13.125C4.82857 14.1605 3.9891 15 2.95357 15H0V0Z"
          fill="#EB7B58"
        />
        <path
          d="M18.5537 0C19.5891 0.000150528 20.4287 0.839559 20.4287 1.875V13.125C20.4287 14.1604 19.5891 14.9998 18.5537 15H7.44629C6.41076 15 5.57129 14.1605 5.57129 13.125V1.875C5.57129 0.839466 6.41076 1.28586e-07 7.44629 0H18.5537ZM14.7861 5.34668C14.5957 5.03352 14.1406 5.03344 13.9502 5.34668L12.3047 8.06152C12.1273 8.3537 11.7118 8.37847 11.501 8.10938L11.0957 7.5918C10.8999 7.34211 10.5219 7.34198 10.3262 7.5918L8.38574 10.0703C8.13498 10.3912 8.36413 10.8603 8.77148 10.8604H13.6514C13.6528 10.8604 13.6541 10.8586 13.6533 10.8574C13.6527 10.8563 13.653 10.8546 13.6543 10.8545H17.2568C17.6379 10.8545 17.8723 10.4382 17.6748 10.1123L14.7861 5.34668ZM11.1963 4.16211C10.6617 4.16218 10.228 4.59995 10.2275 5.13965C10.2275 5.6797 10.6614 6.11809 11.1963 6.11816C11.7311 6.11802 12.165 5.67965 12.165 5.13965C12.1646 4.6 11.7309 4.16225 11.1963 4.16211Z"
          fill="#EB7B58"
        />
      </svg>
    ),
  },
  {
    label: "Popup",
    slug: "popup-builder",
    icon: (
      <svg
        width="21"
        height="15"
        viewBox="0 0 21 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.5 0C20.3284 3.62117e-08 21 0.671573 21 1.5V13.5C21 14.3284 20.3284 15 19.5 15H1.5C0.671575 15 -3.62116e-08 14.3284 0 13.5V1.5C3.62116e-08 0.671574 0.671575 2.29721e-06 1.5 0H19.5ZM13.041 3.20996C12.8895 3.21009 12.7468 3.26882 12.6396 3.37598L6.37598 9.63965C6.26888 9.7468 6.21007 9.88953 6.20996 10.041C6.20996 10.1927 6.26875 10.3361 6.37598 10.4434L7.55664 11.623C7.6638 11.7302 7.80646 11.7899 7.95801 11.79C8.10975 11.79 8.25307 11.7303 8.36035 11.623L8.71582 11.2676L8.22168 10.7734C8.12372 10.6754 8.12392 10.5161 8.22168 10.418C8.31982 10.3198 8.47897 10.3199 8.57715 10.418L9.07227 10.9121L9.45996 10.5244L8.59375 9.6582C8.49566 9.56005 8.49561 9.40088 8.59375 9.30273C8.6919 9.20466 8.85106 9.20467 8.94922 9.30273L9.81543 10.1689L10.2031 9.78125L9.70898 9.28711C9.61089 9.18895 9.61084 9.02882 9.70898 8.93066C9.80716 8.833 9.96642 8.83276 10.0645 8.93066L10.5586 9.42578L10.9463 9.03809L10.0801 8.17188C9.98206 8.07376 9.98206 7.91455 10.0801 7.81641C10.1782 7.71828 10.3374 7.71836 10.4355 7.81641L11.3018 8.68164L11.6826 8.30078L11.1885 7.80664C11.0905 7.70848 11.0904 7.5493 11.1885 7.45117C11.2866 7.35324 11.4458 7.35322 11.5439 7.45117L12.0381 7.94531L12.4258 7.55762L11.5596 6.69238C11.4617 6.59436 11.4618 6.43506 11.5596 6.33691C11.6577 6.23879 11.8178 6.2388 11.916 6.33691L12.7812 7.20215L13.1689 6.81445L12.6748 6.32031C12.5768 6.22215 12.5767 6.06295 12.6748 5.96484C12.7729 5.86678 12.9321 5.86689 13.0303 5.96484L13.5244 6.45898L13.9121 6.07129L13.0469 5.20605C12.9488 5.10792 12.9488 4.94873 13.0469 4.85059C13.145 4.75244 13.3042 4.75249 13.4023 4.85059L14.2676 5.71582L14.624 5.35938C14.731 5.25214 14.79 5.10951 14.79 4.95801C14.79 4.80651 14.731 4.66385 14.624 4.55664L13.4434 3.37598C13.3361 3.26872 13.1927 3.20996 13.041 3.20996ZM12.3867 10.9512L12.9023 11.4658C13.1113 11.6745 13.3892 11.79 13.6846 11.79C13.9799 11.79 14.2579 11.6745 14.4668 11.4658C14.8978 11.0345 14.8979 10.3326 14.4668 9.90137L13.9512 9.38672L12.3867 10.9512ZM11.0928 9.65723L12.0303 10.5947L13.5947 9.03125L12.6572 8.09375L11.0928 9.65723ZM7.59766 6.15527L8.35938 6.90625L9.92383 5.3418L9.16211 4.59082L7.59766 6.15527ZM6.31445 3.21289C6.28585 3.20577 6.25528 3.21364 6.23438 3.23438C6.21366 3.25529 6.20575 3.28585 6.21289 3.31445L6.5918 4.81543C6.66738 5.11413 6.82322 5.38766 7.04102 5.60547L7.24023 5.80176L8.80469 4.2373L8.60547 4.04004C8.38746 3.82228 8.11416 3.66736 7.81543 3.5918L6.31445 3.21289Z"
          fill="#F688DE"
        />
      </svg>
    ),
  },
  {
    label: "Whatsapp Button",
    slug: "whatsapp-button",
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.5 0.00121397C11.5534 -0.0786974 14.9075 3.136 15 7.18871C14.9073 11.2413 11.5533 14.4551 7.5 14.3752C6.54984 14.3771 5.60719 14.2039 4.71973 13.8645L2.97852 14.9524C2.92886 14.9834 2.87106 15.0002 2.8125 15.0002C2.63992 15.0002 2.50001 14.8603 2.5 14.6877V12.5383C0.924219 11.2068 0.0106548 9.25159 0 7.18871C0.0925488 3.13603 3.44663 -0.0786936 7.5 0.00121397ZM4.95605 3.97094L4.94922 3.97192C4.90545 3.97828 3.87616 4.1452 3.69824 5.68774C3.61651 6.3971 3.94525 7.24297 4.6748 8.20043C5.48236 9.26008 6.22447 9.89165 7.32227 10.4514C7.86895 10.7302 8.66773 11.0558 9.41113 11.0559C9.43517 11.0559 9.45958 11.0557 9.4834 11.0549C10.3184 11.0306 10.7642 10.6836 10.9912 10.3967C11.232 10.0925 11.2827 9.79284 11.29 9.74047C11.2948 9.70742 11.3071 9.60295 11.3145 9.33129C11.3247 8.95081 11.1063 8.75132 10.9658 8.68774C10.8864 8.64832 10.0679 8.24202 9.73242 8.07934C9.50591 7.96941 9.17275 7.90664 8.87305 8.23168L8.86914 8.23657C8.82863 8.27195 8.78771 8.32469 8.66602 8.48754V8.48852C8.59088 8.58904 8.44149 8.78891 8.37793 8.85571C8.2435 8.8652 7.79347 8.66748 7.15625 8.17407C6.5281 7.63717 6.31173 7.37794 6.08887 6.96801C6.08657 6.96223 6.08531 6.95189 6.08594 6.94653C6.18423 6.84544 6.34419 6.65974 6.45605 6.52856L6.47461 6.5061C6.73059 6.21875 6.66672 5.88114 6.56738 5.64867C6.42079 5.30587 6.03743 4.43039 6 4.34496C5.94328 4.2016 5.75368 3.97399 5.37305 3.96606C5.07707 3.96005 4.97917 3.96813 4.95605 3.97094Z"
          fill="#47C856"
        />
      </svg>
    ),
  },
  {
    label: "Coupon Popup",
    slug: "coupon-popup",
    icon: (
      <svg
        width="21"
        height="15"
        viewBox="0 0 21 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.5 0C20.3284 3.62119e-08 21 0.671568 21 1.5V13.5C21 14.3284 20.3284 15 19.5 15H1.5C0.671597 15 -3.6211e-08 14.3284 0 13.5V1.5C3.6211e-08 0.671589 0.671598 3.52528e-05 1.5 0H19.5ZM5.96387 7.04883C5.56103 7.15727 5.32196 7.57153 5.42969 7.97461L5.6875 8.93848C6.11174 8.82475 6.54746 9.07773 6.66113 9.50293C6.77461 9.92812 6.52283 10.3646 6.09863 10.4785L6.35645 11.4434C6.46435 11.8464 6.8792 12.0857 7.28223 11.9775L8.08887 11.7607L6.77148 6.83203L5.96387 7.04883ZM14.6494 5.49805C14.5414 5.09509 14.1276 4.85566 13.7246 4.96387L7.23242 6.70801L8.5498 11.6367L15.042 9.89258C15.4451 9.78432 15.6849 9.37003 15.5771 8.9668L15.3193 8.00293C14.8951 8.1168 14.4586 7.86453 14.3447 7.43945C14.2311 7.01433 14.4832 6.5771 14.9072 6.46289L14.6494 5.49805ZM11.4004 7.05859C11.463 6.95053 11.6009 6.91337 11.709 6.97559L11.7305 6.98828C11.8386 7.05095 11.8757 7.18938 11.8135 7.29785L10.4492 9.66699C10.3867 9.77546 10.2479 9.81253 10.1396 9.75L10.1182 9.7373C10.0102 9.67456 9.97289 9.53614 10.0352 9.42773L11.4004 7.05859ZM11.7314 8.50391C11.9435 8.44721 12.1609 8.57364 12.2178 8.78613C12.2746 8.99878 12.1487 9.21743 11.9365 9.27441C11.7245 9.33109 11.507 9.20468 11.4502 8.99219C11.3934 8.77957 11.5193 8.56088 11.7314 8.50391ZM9.91895 7.45508C10.131 7.39818 10.3493 7.52479 10.4062 7.7373C10.463 7.94988 10.337 8.16854 10.125 8.22559C9.913 8.2825 9.69475 8.15669 9.6377 7.94434C9.58085 7.73169 9.70679 7.51208 9.91895 7.45508ZM11.8398 3.2334C11.5505 2.93297 11.0726 2.92455 10.7725 3.21484L7.60645 6.27734L13.2988 4.74805L13.085 4.52637L13.0625 4.54785C12.8206 4.75913 12.4529 4.74476 12.2275 4.51074C11.9951 4.2692 12.0014 3.88448 12.2422 3.65137L11.8398 3.2334Z"
          fill="#CD6BFB"
        />
      </svg>
    ),
  },
  {
    label: "Scratch Card",
    slug: "scratch-card",
    icon: (
      <svg
        width="234"
        height="151"
        viewBox="0 0 234 151"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M178.104 19.4165C158.358 19.4165 142.292 35.4823 142.292 55.2291C142.292 74.976 158.358 91.0418 178.104 91.0418C197.851 91.0418 213.917 74.976 213.917 55.2291C213.917 35.4823 197.851 19.4165 178.104 19.4165Z"
          fill="#FAD34A"
        />
        <path
          d="M217.157 16.1762C206.726 5.74489 192.856 0 178.104 0C163.352 0 149.483 5.74489 139.051 16.1762C128.62 26.6075 122.875 40.4771 122.875 55.2291C122.875 69.9811 128.62 83.8507 139.051 94.2821C149.483 104.713 163.352 110.458 178.104 110.458C192.856 110.458 206.726 104.713 217.157 94.2821C227.589 83.8507 233.333 69.9811 233.333 55.2291C233.333 40.4771 227.589 26.6075 217.157 16.1762ZM178.104 97.5139C154.789 97.5139 135.82 78.5449 135.82 55.2291C135.82 31.9133 154.789 12.9443 178.104 12.9443C201.42 12.9443 220.389 31.9133 220.389 55.2291C220.389 78.5449 201.42 97.5139 178.104 97.5139Z"
          fill="#FAD34A"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M114.479 48.0128C114.214 50.3919 114.08 52.8002 114.08 55.2292C114.08 72.3304 120.74 88.4088 132.832 100.501C142.359 110.028 154.36 116.183 167.387 118.366C164.175 129.959 161.177 140.027 159.746 142.802C155.875 150.312 146.991 152.363 139.96 148.471C134.439 145.416 131.703 139.304 132.662 133.42L127.457 142.802C123.561 149.824 114.702 152.363 107.671 148.471C102.15 145.416 99.4134 139.304 100.373 133.42L95.1671 142.802C91.2707 149.824 82.4117 152.363 75.3805 148.471C69.8595 145.416 67.1232 139.304 68.0825 133.42L62.8779 142.802C58.9816 149.824 50.1226 152.363 43.0914 148.471C37.5703 145.416 34.8341 139.304 35.7934 133.42L30.5888 142.802C26.6924 149.824 17.8335 152.363 10.8023 148.471C3.77108 144.58 1.22929 135.732 5.12568 128.71L24.7307 93.373C20.251 97.7499 13.2652 98.8849 7.50326 95.6958C0.472056 91.8043 -2.06973 82.9566 1.82666 75.9343L19.4419 44.2645C23.3383 37.2422 32.1973 34.7036 39.2285 38.5951C44.944 41.7583 47.6932 48.1958 46.4293 54.2665L55.0139 38.7946C58.9103 31.7724 67.7692 29.2338 74.8004 33.1252C80.3215 36.1806 83.0577 42.2923 82.0985 48.1764L87.3041 38.7946C91.2005 31.7724 100.059 29.2338 107.091 33.1252C112.612 36.1806 115.348 42.2912 114.389 48.1764L114.479 48.0128Z"
          fill="#FAD34A"
        />
      </svg>
    ),
  },
  {
    label: "Spinning Wheel",
    slug: "spinning-wheel",
    icon: (
      <svg
        width="234"
        height="234"
        viewBox="0 0 234 234"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M83.0573 103.015C85.4776 99.4346 88.5557 96.3486 92.1271 93.922L62.3838 21.9198C40.1408 32.8588 22.1515 50.8946 11.2407 73.195L83.0573 103.015Z"
          fill="#EA54C0"
        />
        <path
          d="M216.056 158.264C224.127 134.759 224.127 109.224 216.056 85.7195L144.24 115.54C145.059 119.797 145.059 124.171 144.24 128.428L216.056 158.264Z"
          fill="#EA54C0"
        />
        <path
          d="M92.1271 150.061C88.5557 147.635 85.4776 144.549 83.0573 140.968L11.2407 170.788C22.1515 193.089 40.1408 211.125 62.3838 222.064L92.1271 150.061Z"
          fill="#EA54C0"
        />
        <path
          d="M77.8696 128.436C77.0507 124.179 77.0507 119.804 77.8696 115.547L6.05294 85.7272C-2.01765 109.232 -2.01765 134.767 6.05294 158.272L77.8696 128.436Z"
          fill="#EA54C0"
        />
        <path
          d="M139.052 140.968C136.632 144.549 133.554 147.635 129.982 150.061L159.726 222.064C181.968 211.125 199.958 193.089 210.869 170.788L139.052 140.968Z"
          fill="#EA54C0"
        />
        <path
          d="M117.482 155.263C113.236 156.084 108.873 156.084 104.627 155.263L74.8836 227.265C98.3276 235.356 123.797 235.356 147.241 227.265L117.482 155.263Z"
          fill="#EA54C0"
        />
        <path
          d="M117.482 90.1732C113.236 89.3522 108.873 89.3522 104.627 90.1732L74.8836 18.1709C98.3276 10.0795 123.797 10.0795 147.241 18.1709L117.482 90.1732Z"
          fill="#EA54C0"
        />
        <path
          d="M111.055 101.66C107.044 101.66 103.123 102.852 99.788 105.086C96.453 107.32 93.8537 110.496 92.3188 114.211C90.7839 117.926 90.3823 122.014 91.1648 125.958C91.9473 129.902 93.8787 133.525 96.7149 136.369C99.551 139.212 103.164 141.148 107.098 141.933C111.032 142.717 115.11 142.315 118.815 140.776C122.521 139.237 125.688 136.631 127.917 133.288C130.145 129.944 131.334 126.013 131.334 121.992C131.328 116.601 129.19 111.433 125.388 107.622C121.586 103.81 116.431 101.666 111.055 101.66ZM111.055 128.769C109.718 128.769 108.411 128.372 107.299 127.627C106.187 126.882 105.321 125.824 104.809 124.585C104.298 123.347 104.164 121.984 104.425 120.669C104.686 119.355 105.329 118.147 106.275 117.199C107.22 116.252 108.425 115.606 109.736 115.345C111.047 115.083 112.406 115.217 113.642 115.73C114.877 116.243 115.933 117.112 116.675 118.226C117.418 119.341 117.815 120.651 117.815 121.992C117.814 123.789 117.101 125.512 115.833 126.783C114.566 128.054 112.847 128.768 111.055 128.769Z"
          fill="#EA54C0"
        />
        <path
          d="M216.133 51.1313C207.367 56.5633 181.962 63.6957 170.801 66.6738C168.369 67.3226 166.184 65.1321 166.831 62.6944C169.802 51.5041 176.916 26.033 182.334 17.2448C189.273 5.98764 206.8 7.88741 216.133 17.2448C225.466 26.6021 227.361 44.1744 216.133 51.1313Z"
          fill="#EA54C0"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M174.402 73.2117C180.488 71.5604 189.302 69.0686 197.664 66.3345C200.305 65.4711 202.991 64.5555 205.583 63.6074C207.493 66.7043 209.258 69.9032 210.869 73.195L139.052 103.015C136.632 99.4346 133.554 96.3486 129.982 93.922L159.726 21.9198C163.033 23.5464 166.246 25.3299 169.356 27.261C168.408 29.8659 167.493 32.5656 166.63 35.2194C163.902 43.603 161.417 52.4396 159.77 58.5416C157.356 67.4847 165.482 75.632 174.402 73.2117Z"
          fill="#EA54C0"
        />
      </svg>
    ),
  },
];

// Widget placeholder suggestions with content and style variations
const widgetPlaceholders = {
  quiz: [
    "Create a quiz about Harry Potter with Harry Potter images, styled in dark mode.",
    "Create a quiz with a pink theme about the latest trends in fashion. Make it modern and bold.",
  ],
  form: [
    "Create a contact form for a photography business with a minimalistic white design.",
    "Create a form for a law firm, make it dark grey with clean lines.",
  ],
  booking: [
    "Create a booking widget for a dental office opened from 11am to 5pm. Light blue colors and soft corners.",
    "Create a dark blue booking for a car wash service. Use a modern card layout.",
  ],
  timeline: [
    "Create a timeline about World War 2, with images. Go with vintage tones.",
    "Create a timeline about company history using a clean layout and muted colors.",
  ],
  faq: [
    "Create an FAQ for an e-commerce store with pastel colors and rounded corners.",
    "Create an FAQ for common car issues. Keep it structured and dark-themed.",
  ],
  poll: [
    "Create a poll about favorite ice cream flavors, with images. Soft colors and fun fonts.",
    "Create a poll about the best way to learn programming. Make it techy and minimal.",
  ],
  "countdown-bar": [
    "Create a 30 days countdown until the end of the year using a red alert style.",
    "Create a countdown for a product launch with dark tones and bold fonts.",
  ],
  "image-slider": [
    "Create an image slider for vacation photos in warm, earthy colors.",
    "Create an image slider for a travel agency. Use bright colors and clean transitions.",
  ],
  "popup-builder": [
    "Create a popup for a restaurant coupon for new customers. Use rustic colors and bold call-to-action.",
    "Create a popup for a newsletter subscription. Smooth design with light theme.",
  ],
  "coupon-popup": [
    "Create a coupon popup for a 20% off sale with eye-catching red and sharp corners.",
    "Create a coupon popup for new customers. Keep it modern and green-toned.",
  ],
  "whatsapp-button": [
    "Create a WhatsApp button for customer support in the classic green-white combo.",
    "Create a WhatsApp button for a restaurant. Go with dark theme and thick border.",
  ],
  "spinning-wheel": [
    "Create a spinning wheel for a raffle. Use bold colors and sharp corners.",
    "Create a spinning wheel for a survey. Keep it modern and green-toned.",
  ],
  "scratch-card": [
    "Create a scratch card for a raffle. Use bold colors and sharp corners.",
    "Create a scratch card for a survey. Keep it modern and green-toned.",
  ],
};
// Default generic placeholder for widgets not in the list
const defaultPlaceholders = [
  "Create a widget about...",
  "Create a widget with custom styling...",
];
